import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTL, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, FileDown, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

export default function Orders() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [company, setCompany] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (company !== "all") params.company_id = company;
    const { data } = await api.get("/orders", { params });
    setOrders(data);
    setPage(1);
  };
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    load();
  }, []);
  useEffect(() => { load(); }, [status, company]);

  const filtered = q
    ? orders.filter((x) => (x.order_no || "").toLowerCase().includes(q.toLowerCase()) ||
                            (x.company_name || "").toLowerCase().includes(q.toLowerCase()))
    : orders;
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const downloadPdf = (oid) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${oid}/pdf?auth=${token()}`;
    window.open(url, "_blank");
  };

  const cancelOrder = async (oid) => {
    if (!window.confirm("Sipariş iptal edilirse stok geri yüklenecek ve cari borç düşülecek. Devam edilsin mi?")) return;
    try {
      await api.patch(`/orders/${oid}/cancel`);
      toast.success("Sipariş iptal edildi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal başarısız");
    }
  };

  const removeOrder = async (oid, ono) => {
    if (!window.confirm(`${ono} numaralı siparişi KALICI olarak silmek istediğinize emin misiniz? Stok, cari borç ve ödemeler de silinecektir.`)) return;
    try {
      await api.delete(`/orders/${oid}`);
      toast.success("Sipariş silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 lg:p-5">
        <div className="grid lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Ara</label>
            <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Sipariş no, firma..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Firma</label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Durum</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="approved">Onaylandı</SelectItem>
                <SelectItem value="shipped">Sevk Edildi</SelectItem>
                <SelectItem value="completed">Tamamlandı</SelectItem>
                <SelectItem value="cancelled">İptal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="text-[#08285A] font-bold">{filtered.length}</span> sipariş
        </div>
        <div className="flex gap-2">
          <ExportButton type="orders" format="pdf" label="PDF İndir" testid="orders-pdf"
                        icon={<FileDown size={14} className="text-[#DC2626]" />} />
          <ExportButton type="orders" format="xlsx" label="Excel İndir" testid="orders-xlsx"
                        icon={<FileDown size={14} className="text-[#16A34A]" />} />
          <ExportButton type="orders" format="csv" label="CSV İndir" testid="orders-csv" />
          <Button onClick={() => navigate("/orders/new")} data-testid="new-order-btn"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> Yeni Sipariş
          </Button>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Sipariş No</th>
                <th className="text-left px-4 py-3 font-semibold">Firma</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Vade</th>
                <th className="text-right px-4 py-3 font-semibold">Tutar</th>
                <th className="text-right px-4 py-3 font-semibold">Ödenen</th>
                <th className="text-right px-4 py-3 font-semibold">Kalan</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="text-center px-4 py-3 font-semibold">Vade</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-[#6B7280]">Sipariş bulunamadı</td></tr>
              ) : paged.map((o) => (
                <tr key={o.id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                    data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 font-bold text-[#0B3676] font-mono text-xs">{o.order_no}</td>
                  <td className="px-4 py-3 font-semibold text-[#111827]">{o.company_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(o.order_date)}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(o.due_date)}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#08285A]">{fmtTL(o.total_amount)}</td>
                  <td className="px-4 py-3 text-right text-[#16A34A] font-semibold">{fmtTL(o.paid_amount)}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#DC2626]">{fmtTL(o.remaining_amount)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-center">
                    {o.vade_status && <StatusBadge status={o.vade_status} />}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => downloadPdf(o.id)}
                            data-testid={`pdf-order-${o.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded"
                            title="PDF İndir"><FileDown size={14} /></button>
                    {user?.role === "admin" && o.status !== "cancelled" && (
                      <button onClick={() => cancelOrder(o.id)}
                              data-testid={`cancel-order-${o.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                              title="İptal"><X size={14} /></button>
                    )}
                    {user?.role === "admin" && (
                      <button onClick={() => removeOrder(o.id, o.order_no)}
                              data-testid={`delete-order-${o.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                              title="Sil"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
            <div className="text-[#6B7280]">Sayfa {page} / {totalPages}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Önceki</Button>
              <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Sonraki</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
